import {
  BrandApiClientCredentialsConfiguration,
  LocationApiClientCredentialsConfiguration,
  RoomApiClientCredentialsConfiguration,
} from '@era/settings-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface ApiClientCredentialsConfiguration {
  type: CategoryType;
  id: string;
  apiClientCredentialsId: string;
}

type AnyApiClientCredentialsConfiguration =
  | BrandApiClientCredentialsConfiguration
  | LocationApiClientCredentialsConfiguration
  | RoomApiClientCredentialsConfiguration;

/**
 * Generic function to convert any API client credentials configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function apiClientCredentialsConfigurationFrom(
  configuration: AnyApiClientCredentialsConfiguration
): ApiClientCredentialsConfiguration {
  // Type guard functions to determine the category type
  if ('brandId' in configuration) {
    return {
      type: 'Brand',
      id: configuration.brandId,
      apiClientCredentialsId: configuration.apiClientCredentialsId,
    };
  }
  if ('locationId' in configuration) {
    return {
      type: 'Location',
      id: configuration.locationId,
      apiClientCredentialsId: configuration.apiClientCredentialsId,
    };
  }
  if ('roomId' in configuration) {
    return {
      type: 'Room',
      id: configuration.roomId,
      apiClientCredentialsId: configuration.apiClientCredentialsId,
    };
  }
  throw new Error('Unknown API client credentials configuration type');
}

export type CategoryApiClientSettingState = EntityState<ApiClientCredentialsConfiguration>;

export function selectId(configuration: ApiClientCredentialsConfiguration) {
  return `${configuration.type}-${configuration.id}`;
}

export const adapter: EntityAdapter<ApiClientCredentialsConfiguration> =
  createEntityAdapter<ApiClientCredentialsConfiguration>({
    selectId,
  });

export const initialState: CategoryApiClientSettingState = adapter.getInitialState();

/**
 * Partial state interface for components that use category API client setting store
 * Includes both categoryApiClientSetting and apiClientSetting feature states
 */
export interface CategoryApiClientSettingPartialState {
  categoryApiClientSetting: CategoryApiClientSettingState;
  apiClientSetting: import('../api-client-setting/api-client-setting.state').ApiClientSettingState;
}
